import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getYear, subYears } from 'date-fns';
import type { TFunction } from 'i18next';
import { BoxIcon, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Estate } from '@/features/estates-property/api/estate';
import { cn } from '@/lib/utils';
import { getNumberFormatter } from '@/utils/getNumberFormatter';
import { getPriceWithCurrency } from '@/utils/getPriceWithCurrency';

import type { EstateAnalysis } from '../api/analytics';
import { useSocioEconomics } from '../api/socio-economics';
import { DynamicChartRenderer } from './charts/dynamic-chart-render';
import { PrintContainer } from './print-container';
import { PrintableChart } from './printable-chart';
import { TrendArrow } from './trend-arrow';
import type { ExportPdfRef } from './types/export-pdf';

const TABS = [
  { id: 'household_buying_power', label: 'householdBuyingPower' },
  { id: 'income_levels', label: 'incomeLevels' },
  { id: 'household_structure', label: 'householdStructure' },
  { id: 'population_size', label: 'populationSize' },
  { id: 'age_groups', label: 'ageGroups' },
];

const isNumeric = (value: string | number) =>
  !Number.isNaN(Number(value)) && !Number.isNaN(parseFloat(String(value)));

const getTitle = (t: TFunction<'translation', undefined>, label: string, multiply = 1000) => {
  const [label1, label2] = label.split('_');
  return `${t(label1, {
    defaultValue: isNumeric(label1)
      ? getPriceWithCurrency(Number(label1) * multiply, 'EUR', 'en-US', {
          maximumFractionDigits: 0,
        })
      : label1,
  })} - ${getPriceWithCurrency(Number(label2) * multiply, 'EUR', 'en-US', { maximumFractionDigits: 0 })}`;
};

const getAgeGroupTitle = (label: string) => {
  const [label1, label2] = label.split('_');
  if (label2 === 'plus') {
    return `>${label1}`;
  } else {
    return `${label1} - ${label2}`;
  }
};

const getHouseholdStructureTitle = (t: TFunction<'translation', undefined>, label: string) => {
  switch (label) {
    // These categories are static and defined by PriceHub
    case 'one_person':
      return t('onePeople');
    case 'two_persons':
      return t('multiplePeople', { number: 2 });
    case 'three_persons':
      return t('multiplePeople', { number: 3 });
    case 'four_persons':
      return t('multiplePeople', { number: 4 });
    case 'five_persons':
      return t('multiplePeople', { number: 5 });
    case 'six_larger_persons':
      return t('morePeople', { number: 6 });
    default:
      return t(label);
  }
};

const hasPositiveValue = (value: unknown): value is number =>
  typeof value === 'number' && value > 0;

type SocioEconomicsProps = {
  data: Estate;
  estateAnalysis: EstateAnalysis;
  hideOnPrint?: boolean;
  isLastPrintPage?: boolean;
};

export const SocioEconomics = forwardRef<ExportPdfRef, SocioEconomicsProps>(
  ({ data: estate, estateAnalysis, hideOnPrint = false, isLastPrintPage = false }, ref) => {
    const { t } = useTranslation();

    const chartRefs = {
      householdBuyingPower: useRef<ExportPdfRef>(null),
      incomeLevels: useRef<ExportPdfRef>(null),
      householdStructure: useRef<ExportPdfRef>(null),
      populationSize: useRef<ExportPdfRef>(null),
      ageGroups: useRef<ExportPdfRef>(null),
    };

    const { data, currentYear } = useSocioEconomics(estateAnalysis);

    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [activeTab, setActiveTab] = useState(TABS[0]);

    const yearOptions = useMemo(() => {
      return Array.from({ length: 4 }, (_, i) => currentYear - i);
    }, [currentYear]);

    const getChartData = useCallback(
      (id: string) => {
        if (!data) return [];

        const divisionsYearsData = data[id]?.divisions?.[0]?.years || [];

        if (id === 'population_size') {
          return divisionsYearsData.flatMap((yearData, index) => {
            const currentValue = yearData.values[0];

            if (!hasPositiveValue(currentValue?.absoluteValue)) {
              return [];
            }

            const prevValue = index !== 0 ? divisionsYearsData[index - 1] : undefined;

            return {
              ...currentValue,
              value: currentValue.absoluteValue,
              title: yearData.year,
              changePercent: hasPositiveValue(prevValue?.values[0]?.absoluteValue)
                ? ((currentValue.absoluteValue - prevValue.values[0].absoluteValue) * 100) /
                  prevValue.values[0].absoluteValue
                : undefined,
            };
          });
        }

        const selectedYearData = divisionsYearsData.find?.((item) => item.year === selectedYear);

        if (!selectedYearData) return [];

        return selectedYearData.values
          .filter(
            (value) =>
              hasPositiveValue(value.absoluteValue) || hasPositiveValue(value.relativeValue)
          )
          .map((value) => ({
            ...value,
            value: value.relativeValue * 100,
            title:
              id === 'household_structure'
                ? getHouseholdStructureTitle(t, value.subtype)
                : id === 'age_groups'
                  ? getAgeGroupTitle(value.subtype)
                  : getTitle(t, value.subtype, id === 'income_levels' ? 1 : 1000),
          }));
      },
      [t, data, selectedYear]
    );

    const chartDataByTabId = useMemo<Record<string, ReturnType<typeof getChartData>>>(() => {
      return {
        household_buying_power: getChartData('household_buying_power'),
        income_levels: getChartData('income_levels'),
        household_structure: getChartData('household_structure'),
        population_size: getChartData('population_size'),
        age_groups: getChartData('age_groups'),
      };
    }, [getChartData]);

    const hasFirstPrintPageData =
      chartDataByTabId.age_groups.length > 0 || chartDataByTabId.population_size.length > 0;
    const hasSecondPrintPageData =
      chartDataByTabId.household_buying_power.length > 0 ||
      chartDataByTabId.income_levels.length > 0 ||
      chartDataByTabId.household_structure.length > 0;

    const activeChartData = chartDataByTabId[activeTab.id] ?? [];
    const hasActiveChartData = activeChartData.length > 0;

    const populationSizeMetrics = useMemo(() => {
      if (!data) return undefined;

      const divisionsYearsData = data.population_size?.divisions?.[0]?.years || [];

      const fourYearsAgoYear = getYear(subYears(new Date(), 4));

      const currentYearValue =
        divisionsYearsData.find?.((item) => item.year === currentYear)?.values?.[0]
          ?.absoluteValue || 0;

      const fourYearsAgoYearValue =
        divisionsYearsData.find?.((item) => item.year === fourYearsAgoYear)?.values?.[0]
          ?.absoluteValue || undefined;

      const changePercent = fourYearsAgoYearValue
        ? ((currentYearValue - fourYearsAgoYearValue) * 100) / fourYearsAgoYearValue
        : undefined;

      return {
        currentYearValue: hasPositiveValue(currentYearValue) ? currentYearValue : undefined,
        changePercent,
      };
    }, [data, currentYear]);

    useImperativeHandle(ref, () => ({
      startExportPdf: async () => {
        const exportPdfProcesses = Object.values(chartRefs).map((el) =>
          el.current?.startExportPdf?.()
        );
        await Promise.all(exportPdfProcesses);
      },
      finishExportPdf: async () => {
        Object.values(chartRefs).map((el) => el.current?.finishExportPdf?.());
      },
    }));

    return (
      <div className="print:!m-0">
        <div className="print:hidden">
          <h1 className="mb-6 text-md font-bold text-primary-dark">{t('socioEconomics')}</h1>
          <Card>
            <div className="grid grid-cols-5 bg-secondary">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={cn(
                    'relative cursor-pointer select-none',
                    activeTab.id === tab.id && 'rounded-tl-md rounded-tr-md bg-white'
                  )}
                  style={{
                    boxShadow: activeTab.id === tab.id ? '4px 4px 15px 0px #00000026' : 'none',
                  }}
                  onClick={() => setActiveTab(tab)}
                >
                  <div className="px-11 py-9">
                    <h2 className="text-md font-bold text-[#003340]">{t(tab.label)}</h2>
                  </div>
                </button>
              ))}
            </div>

            <div className="relative z-10 flex w-full gap-x-4 bg-white p-11">
              <div className="flex max-w-44 flex-col gap-y-4">
                <Card className="rounded-md bg-card-foreground p-4 text-white">
                  <div className="flex items-center gap-1">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F5F6FF10]">
                      <BoxIcon size={14} />
                    </div>
                    <div>
                      <p className="text-sm font-[300] leading-normal">{t('populationSize')}</p>
                      <p className="text-sm font-[300] leading-normal">
                        {t('currentYearOverYears', {
                          currentYear: currentYear,
                          years: 4,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <p className="font-bold text-[16p]">
                      {populationSizeMetrics?.currentYearValue
                        ? getNumberFormatter(populationSizeMetrics.currentYearValue, 'en-GB', {
                            maximumFractionDigits: 0,
                            minimumFractionDigits: 0,
                          })
                        : '----'}
                    </p>
                    {populationSizeMetrics?.changePercent ? (
                      <div className="flex items-center gap-2 rounded-lg bg-[#29C9AC10] px-2">
                        <TrendArrow changeValue={populationSizeMetrics.changePercent} />
                        <p className="text-sm font-normal">
                          {populationSizeMetrics.changePercent.toFixed(2)}%
                        </p>
                      </div>
                    ) : null}
                  </div>
                </Card>
                <p className="text-sm font-normal leading-normal">
                  {t(`${activeTab.label}Description`, {
                    defaultValue: t('populationSizeDescription'),
                  })}
                </p>
              </div>
              <div className="flex-1">
                <DropDownMenu
                  activeTab={activeTab.id}
                  yearOptions={yearOptions}
                  selectedYear={selectedYear}
                  setSelectedYear={setSelectedYear}
                />

                {hasActiveChartData ? (
                  <DynamicChartRenderer
                    activeTab={activeTab.id}
                    chartData={activeChartData}
                    height={activeTab.id === 'population_size' ? 256 : 220}
                  />
                ) : (
                  <p className="py-16 text-center text-sm text-text-secondary">
                    {t('dataNotAvailableForSelectedYear')}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        <PrintContainer
          estate={estate}
          header={t('socioEconomics')}
          hide={hideOnPrint || !hasFirstPrintPageData}
          isLastPage={isLastPrintPage || !hasSecondPrintPageData}
        >
          {chartDataByTabId.age_groups.length ? (
            <>
              <h3 className="mb-4 mt-6 hidden font-bold print:block">{t('ageGroups')}</h3>
              <PrintableChart ref={chartRefs.ageGroups} defaultDisplay="none">
                <DynamicChartRenderer
                  activeTab="age_groups"
                  chartData={chartDataByTabId.age_groups}
                  height={300}
                  fontSize={24}
                  barSize={40}
                  margin={{ left: 150, right: 100 }}
                />
              </PrintableChart>
            </>
          ) : null}
          {chartDataByTabId.population_size.length ? (
            <>
              <h3 className="mb-4 mt-24 hidden font-bold print:block">{t('populationSize')}</h3>
              <PrintableChart ref={chartRefs.populationSize} defaultDisplay="none">
                <DynamicChartRenderer
                  activeTab="population_size"
                  chartData={chartDataByTabId.population_size}
                  height={420}
                  fontSize={24}
                  margin={{ left: 200 }}
                />
              </PrintableChart>
            </>
          ) : null}
        </PrintContainer>
        <PrintContainer
          estate={estate}
          header={t('socioEconomics')}
          hide={hideOnPrint || !hasSecondPrintPageData}
          isLastPage={isLastPrintPage}
        >
          {chartDataByTabId.household_buying_power.length ? (
            <>
              <h3 className="mb-4 mt-6 hidden font-bold print:block">
                {t('householdBuyingPower')}
              </h3>
              <PrintableChart ref={chartRefs.householdBuyingPower} defaultDisplay="none">
                <DynamicChartRenderer
                  activeTab="household_buying_power"
                  chartData={chartDataByTabId.household_buying_power}
                  height={240}
                  fontSize={24}
                  barSize={40}
                  margin={{ left: 300, right: 100 }}
                />
              </PrintableChart>
            </>
          ) : null}
          {chartDataByTabId.income_levels.length ? (
            <>
              <h3 className="mb-4 mt-24 hidden font-bold print:block">{t('incomeLevels')}</h3>
              <PrintableChart ref={chartRefs.incomeLevels} defaultDisplay="none">
                <DynamicChartRenderer
                  activeTab="income_levels"
                  chartData={chartDataByTabId.income_levels}
                  height={240}
                  fontSize={24}
                  barSize={40}
                  margin={{ left: 300, right: 100 }}
                />
              </PrintableChart>
            </>
          ) : null}
          {chartDataByTabId.household_structure.length ? (
            <>
              <h3 className="mb-4 mt-24 hidden font-bold print:block">{t('householdStructure')}</h3>
              <PrintableChart ref={chartRefs.householdStructure} defaultDisplay="none">
                <DynamicChartRenderer
                  activeTab="household_structure"
                  chartData={chartDataByTabId.household_structure}
                  height={320}
                  fontSize={24}
                  margin={{ left: 50, top: 20, bottom: 20 }}
                />
              </PrintableChart>
            </>
          ) : null}
        </PrintContainer>
      </div>
    );
  }
);

const DropDownMenu = ({
  activeTab,
  yearOptions,
  selectedYear,
  setSelectedYear,
}: {
  activeTab: string;
  yearOptions: number[];
  selectedYear: number;
  setSelectedYear: React.Dispatch<React.SetStateAction<number>>;
}) => {
  if (activeTab === 'population_size') return null;
  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline">
            <span>{selectedYear}</span>
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {yearOptions.map((y) => (
            <DropdownMenuItem key={y} onSelect={() => setSelectedYear(y)}>
              <span>{y}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
